import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import {
  fetchCollectionReceipts,
  fetchCollectionSummary,
} from '#/api/crmMgmt/customerOrder/collection';
import { PERM } from '#/config/permissions';
import CollectionAllocationsCard
  from '#/modules/crmMgmt/customerOrder/components/collection/CollectionAllocationsCard';
import CollectionCashEventsCard
  from '#/modules/crmMgmt/customerOrder/components/collection/CollectionCashEventsCard';
import CollectionLegacyCard from '#/modules/crmMgmt/customerOrder/components/collection/CollectionLegacyCard';
import CollectionPlanCard from '#/modules/crmMgmt/customerOrder/components/collection/CollectionPlanCard';
import CollectionReceiptsCard from '#/modules/crmMgmt/customerOrder/components/collection/CollectionReceiptsCard';
import CollectionSummaryCard from '#/modules/crmMgmt/customerOrder/components/collection/CollectionSummaryCard';
import { collectionQueryKey } from '#/modules/crmMgmt/customerOrder/components/collection/collectionQueryKeys';
import { getCustomerOrderApiErrorMessage } from '#/modules/crmMgmt/customerOrder/helper/apiError';
import { mapReceiptRow } from '#/modules/crmMgmt/customerOrder/helper/collection';
import { useCan } from '#/modules/infra/auth/useCan';

function SectionHeading({ children }) {
  return <h3 className="border-t border-slate-200 px-4 pt-3 text-sm font-medium text-slate-700">{children}</h3>;
}

SectionHeading.propTypes = { children: PropTypes.node.isRequired };

/**
 * 客訂「收款」分頁：計畫（業務可讀）、彙總（彙總或財務權限）、
 * 實收／子單分配／退款沖銷／歷史核對（財務）。
 * 一般客訂 read 權限看不到任何實收紀錄。
 */
const CustomerOrderCollectionSection = ({ detail }) => {
  const customerOrderId = detail.id;
  const canReadFinance = useCan(PERM.FMS_CUSTOMER_COLLECTION_READ);
  const canReadSummary = useCan(PERM.CUSTOMER_COLLECTION_SUMMARY_READ);

  const receiptsQuery = useQuery({
    queryKey: collectionQueryKey(customerOrderId, 'receipts'),
    queryFn: () => fetchCollectionReceipts(customerOrderId),
    enabled: canReadFinance,
  });
  const summaryQuery = useQuery({
    queryKey: collectionQueryKey(customerOrderId, 'summary'),
    queryFn: () => fetchCollectionSummary(customerOrderId),
    enabled: !canReadFinance && canReadSummary,
  });

  const summary = canReadFinance
    ? receiptsQuery.data?.data?.summary
    : summaryQuery.data?.data;
  const activeError = receiptsQuery.error ?? summaryQuery.error;

  return (
    <div className="space-y-3 pb-3">
      <CollectionPlanCard detail={detail} />

      {canReadFinance || canReadSummary ? (
        <>
          <SectionHeading>收款彙總</SectionHeading>
          {activeError ? (
            <p className="px-4 text-sm text-destructive">
              {getCustomerOrderApiErrorMessage(activeError, '載入收款資料失敗')}
            </p>
          ) : null}
          <CollectionSummaryCard summary={summary} />
        </>
      ) : null}

      {canReadFinance ? (
        <>
          <SectionHeading>實收紀錄</SectionHeading>
          <CollectionReceiptsCard
            detail={detail}
            receipts={receiptsQuery.data?.data?.receipts ?? []}
            summary={summary}
          />
          <SectionHeading>子單分配</SectionHeading>
          <CollectionAllocationsCard
            detail={detail}
            receipts={(receiptsQuery.data?.data?.receipts ?? []).map(mapReceiptRow)}
          />
          <SectionHeading>退款與沖銷</SectionHeading>
          <CollectionCashEventsCard
            detail={detail}
            receipts={(receiptsQuery.data?.data?.receipts ?? []).map(mapReceiptRow)}
            summary={summary}
          />
          <CollectionLegacyCard
            detail={detail}
            heading={<SectionHeading>歷史 PAID 核對</SectionHeading>}
          />
        </>
      ) : null}
    </div>
  );
};

CustomerOrderCollectionSection.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
    salesOrders: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

export default CustomerOrderCollectionSection;
